import { csx, Divider } from '@vtex/admin-ui'
import React, { useMemo } from 'react'
import { useLazyLoadQuery } from 'react-relay-offline'
import { graphql } from 'babel-plugin-relay/macro'
import { useCartSimulation } from '@/core/globalState/cartSimulation'
import type { CountryCode } from '@/__generated__/schema-types'

import {
  ProductName,
  ProductSkuPrice,
  ProductSkuRef,
  ProductSkuPromotions,
  ProductSkuImage,
  ProductSkuHighlightedPromotion,
  ProductSkuAvailability,
  ProductPageRoot,
  ProductSkuSelection,
  ProductSkuPaymentData,
  ProductDetailsSectionDesktop,
  ProductDetailsSectionMobile,
} from './components'
import type { NewProductPageQuery } from './__generated__/NewProductPageQuery.graphql'
import { useProductPageUrlParams } from './hooks'
import { Page } from '../../ui/components'
import { DefaultTopbar } from '../../core/components'
import { useCustomConfig } from '../../core/hooks/useCustomConfig'
import { ProductSkuSimulationLocation } from './components/ProductSkuAvailability/ProductSkuSimulationLocation'

const newProductPageQuery = graphql`
  query NewProductPageQuery(
    $id: ID!
    $addresses: [SimulationAddressInput!]!
    $paymentMethod: PaymentMethodWhereInput
  ) {
    product(id: $id) {
      name
      ...ProductSkuRef_Product
      ...ProductSkuImage_Product
      ...ProductSkuPrice_Product
      ...ProductSkuPromotions_Product
      ...ProductSkuHighlightedPromotion_Product
      ...ProductSkuSelection_Product
      ...ProductSkuAvailability_Product
      ...ProductSkuPaymentData_Product
      ...ProductDetailsDrawer_Product
    }
  }
`

export const NewProductPageScreen = () => {
  const { productId } = useProductPageUrlParams()
  const { payments } = useCustomConfig()
  const {
    cartSimulation: { simulationAddress },
  } = useCartSimulation()

  const addressVariable = useMemo(() => {
    if (!simulationAddress) {
      return null
    }

    return {
      addressId: simulationAddress.addressId,
      addressType: simulationAddress.addressType,
      countryCode: simulationAddress.country as CountryCode,
      postalCode: simulationAddress.postalCode,
      geoCoordinates: simulationAddress.geoCoordinates,
    }
  }, [simulationAddress])

  const { data } = useLazyLoadQuery<NewProductPageQuery>(
    newProductPageQuery,
    {
      id: productId,
      // We can assert non-null because of the "skip" condition below.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      addresses: [addressVariable!],
      paymentMethod: {
        id: {
          in: payments.filters,
        },
      },
    },
    {
      fetchPolicy: 'store-and-network',
      UNSTABLE_renderPolicy: 'partial',
      skip: !addressVariable,
    }
  )

  if (!data?.product) {
    return null
  }

  return (
    <Page>
      <DefaultTopbar />
      <ProductPageRoot>
        <div className={csx({ position: 'relative' })}>
          <ProductSkuHighlightedPromotion productRef={data.product} />
          <ProductSkuImage productRef={data.product} />
          <ProductDetailsSectionDesktop productDescription={data.product} />
        </div>
        <div>
          <ProductSkuPromotions productRef={data.product} />
          <ProductSkuRef productRef={data.product} />
          <ProductName>{data.product.name}</ProductName>
          <div className={csx({ marginBottom: '$space-8' })}>
            <ProductSkuPrice productRef={data.product} />
            <ProductSkuPaymentData productRef={data.product} />
          </div>
          <ProductSkuSelection productRef={data.product} />

          <div
            className={csx({
              marginTop: '$space-8',
              border: '$neutral',
              borderRadius: '4px',
            })}
          >
            <ProductSkuSimulationLocation address={simulationAddress} />

            <Divider />

            <ProductSkuAvailability productRef={data.product} />
          </div>

          <ProductDetailsSectionMobile productDescription={data.product} />
        </div>
      </ProductPageRoot>
    </Page>
  )
}
